import type { PostHog } from "posthog-js";

const DEFAULT_FLAG_WAIT_MS = 1200;

let decideTimedOut = false;
let identifiedAccountId: string | null = null;

type PostHogWithReload = PostHog & {
  reloadFeatureFlags?: (callback?: () => void) => void;
};

/** True after the first flag wait times out this session (ad-block or slow /decide). */
export function isPostHogDecideBlocked(): boolean {
  return decideTimedOut;
}

/**
 * Identify once per account before reading flags in beforeLoad.
 * Returns true only when identify was actually called this invocation, so the
 * caller can scope a follow-up `reloadFeatureFlags()` to the one navigation
 * where the anonymous flag snapshot needs to be discarded.
 */
export function ensurePostHogIdentified(
  posthog: PostHog,
  accountId: string | undefined
): boolean {
  if (!accountId || identifiedAccountId === accountId) {
    return false;
  }
  identifiedAccountId = accountId;
  posthog.identify(accountId);
  return true;
}

/**
 * Waits until feature flags are ready for routing, or until timeout.
 * When `reloadAfterIdentify` is set, uses `reloadFeatureFlags(callback)` so we
 * do not resolve on a stale anonymous snapshot from `onFeatureFlags`.
 */
export function waitForPostHogFlags(
  posthog: PostHog,
  options?: { timeoutMs?: number; reloadAfterIdentify?: boolean }
): Promise<void> {
  const timeoutMs = options?.timeoutMs ?? DEFAULT_FLAG_WAIT_MS;

  if (decideTimedOut) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    let settled = false;
    let unsubscribe: (() => void) | undefined;

    const settle = () => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      unsubscribe?.();
      resolve();
    };

    const timer = setTimeout(() => {
      decideTimedOut = true;
      settle();
    }, timeoutMs);

    const posthogWithReload = posthog as PostHogWithReload;

    if (options?.reloadAfterIdentify && posthogWithReload.reloadFeatureFlags) {
      posthogWithReload.reloadFeatureFlags(() => settle());
      return;
    }

    unsubscribe = posthog.onFeatureFlags(() => settle());
  });
}
